import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ToastContainer, toast } from 'react-toastify';
import request from '../../../utils/Request.utils';
import ErrorPage from '../error/ErrorPage';
import { isDetailNotFound, isValidDetailId } from '../../../utils/DetailPage.utils';

const NOTICE_TITLE_MAX_LENGTH = 100;
const NOTICE_CONTENT_MAX_LENGTH = 1000;
const limitNoticeTitle = (value) => value.slice(0, NOTICE_TITLE_MAX_LENGTH);
const limitNoticeContent = (value) => value.slice(0, NOTICE_CONTENT_MAX_LENGTH);
const getNoticeTextCount = (value, maxLength) => `${value.length.toLocaleString()} / ${maxLength.toLocaleString()}자`;

const NoticeWrite = () => {
  const navigate = useNavigate();
  const { board_seq } = useParams();

  const [notFound, setNotFound] = useState(false);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [isMain, setIsMain] = useState(false);

  useEffect(() => {
    let active = true;
    setNotFound(false);

    if (!board_seq) return;
    if (!isValidDetailId(board_seq)) {
      setNotFound(true);
      return;
    }

    const handleError = (error) => {
      if (!active) return;
      if (isDetailNotFound(error)) {
        setNotFound(true);
        return;
      }
      toast.error("공지사항 불러오기 중 오류가 발생했습니다.");
    };

    const fetchBoardData = async () => {
      try {
        const successHandler = (response) => {
          if (!active) return;
          const data = response?.data;

          if (isDetailNotFound(response, data)) {
            setNotFound(true);
            return;
          }
          if (response?.messageCode !== 200) {
            handleError();
            return;
          }

          setTitle(data.board_title ?? '');
          setContent(data.board_content ?? '');
          setIsMain(data.board_is_main === 'true');
        };

        await request.get(`/board/list/admin/BD_NOTICE/${board_seq}`, {}, successHandler, handleError);
      } catch (error) {
        handleError(error);
      }
    };

    fetchBoardData();
    return () => { active = false; };
  }, [board_seq]);

  const handleTitleChange = (e) => setTitle(limitNoticeTitle(e.target.value));
  const handleContentChange = (e) => setContent(limitNoticeContent(e.target.value));
  const handleIsMainChange = () => setIsMain(!isMain);

  const getResponseMessage = (response, fallbackMessage) => {
    return response?.data?.message || response?.message || response?.messageString || fallbackMessage;
  };

  const completeSubmit = () => {
    toast.success(`공지사항이 성공적으로 ${board_seq ? "수정" : "등록"}되었습니다.`);
    navigate("/Board/Notice");
  };

  const handleSubmit = async () => {
    if (!title.trim()) {
      toast.error("제목을 입력해 주세요.");
      return;
    }

    if (!content.trim()) {
      toast.error("내용을 입력해 주세요.");
      return;
    }

    if (title.length > NOTICE_TITLE_MAX_LENGTH || content.length > NOTICE_CONTENT_MAX_LENGTH) {
      toast.error(`제목은 ${NOTICE_TITLE_MAX_LENGTH.toLocaleString()}자, 내용은 ${NOTICE_CONTENT_MAX_LENGTH.toLocaleString()}자 이하로 입력해 주세요.`);
      return;
    }

    const data = {
      board_title: title,
      board_content: content,
      board_is_main: String(isMain),
    };
    
    try {
      const successHandler = async (response) => {
        if (response.messageCode === 200) {
          completeSubmit();
        } else if (response.messageCode === 411) {
          toast.info(getResponseMessage(response, "변경사항이 없습니다"));
        } else {
          toast.error(getResponseMessage(response, `공지사항 ${board_seq ? "수정" : "등록"} 중 오류가 발생했습니다.`));
        }
      };

      const errorHandler = (error) => {
        toast.error(`공지사항 ${board_seq ? "수정" : "등록"} 중 오류가 발생했습니다.`);
      };

      if (board_seq) {
        console.log('수정')
        await request.put(`/board/BD_NOTICE/${board_seq}`, data, successHandler, errorHandler);
      } else {
        console.log('등록')
        await request.put("/board/BD_NOTICE", data, successHandler, errorHandler);
      }

    } catch (error) {
      toast.error(`공지사항 ${board_seq ? "수정" : "등록"} 중 오류가 발생했습니다.`);
    }
  };


  if (notFound) return <ErrorPage />;

  return (
    <main>
      <h2 className="adm_com_pg_tit">공지사항 {board_seq ? "수정" : "등록"}</h2>
      <div className="adm_com_pg_content">
        <table className='adm_com_board_list type_row'>
          <colgroup>
            <col width="10%" /><col />
          </colgroup>
          <tbody>
            <tr>
              <th>작성자</th>
              <td>관리자</td>
            </tr>
            <tr>
              <th>메인공지여부</th>
              <td>
                <section className="check">
                  <input
                    type="checkbox"
                    id="chk01"
                    className="adm_com_chk"
                    checked={isMain}
                    onChange={handleIsMainChange}
                  />
                  <label htmlFor="chk01">메인공지</label>
                </section>
              </td>
            </tr>
            <tr>
              <th>제목</th>
              <td>
                <input
                  type="text"
                  className="input"
                  value={title}
                  onChange={handleTitleChange}
                  maxLength={NOTICE_TITLE_MAX_LENGTH}
                />
                <p className="adm_char_count">{getNoticeTextCount(title, NOTICE_TITLE_MAX_LENGTH)}</p>
              </td>
            </tr>
            <tr>
              <th>내용</th>
              <td>
                <textarea
                  className="textarea"
                  value={content}
                  onChange={handleContentChange}
                  rows="10"
                  maxLength={NOTICE_CONTENT_MAX_LENGTH}
                />
                <p className="adm_char_count">{getNoticeTextCount(content, NOTICE_CONTENT_MAX_LENGTH)}</p>
              </td>
            </tr>
          </tbody>
        </table>
        <div className="adm_com_btn_wrap center">
          <button className="adm_com_btn blue" onClick={handleSubmit}>{board_seq ? "수정" : "등록"}</button>
          <button className="adm_com_btn gray" onClick={() => navigate("/Board/Notice")}>취소</button>
        </div>
      </div>
      <ToastContainer />
    </main>
  );
};

export default NoticeWrite;
